import { useEffect } from "react";
import { PAGES, canonicalUrl } from "@/seo/pages";
import { breadcrumbSchema, pageCrumbs } from "@/seo/schema";
import JsonLd from "@/components/JsonLd";

const upsert = (selector, tagName, attrs) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement(tagName);
    document.head.appendChild(tag);
  }
  Object.entries(attrs).forEach(([k, v]) => tag.setAttribute(k, v));
};

const remove = (selector) => document.head.querySelector(selector)?.remove();

/** Head updater. `path` pulls title/description from seo/pages.js and sets the canonical. */
export const SEO = ({ path, title, description, noindex = false, crumbs }) => {
  const page = path ? PAGES[path] : null;
  const t = title || page?.title;
  const d = description || page?.description;
  const canonical = path && !noindex ? canonicalUrl(path) : null;

  useEffect(() => {
    if (t) {
      document.title = t;
      upsert('meta[property="og:title"]', "meta", { property: "og:title", content: t });
      upsert('meta[name="twitter:title"]', "meta", { name: "twitter:title", content: t });
    }
    if (d) {
      upsert('meta[name="description"]', "meta", { name: "description", content: d });
      upsert('meta[property="og:description"]', "meta", { property: "og:description", content: d });
      upsert('meta[name="twitter:description"]', "meta", { name: "twitter:description", content: d });
    }
    if (canonical) {
      upsert('link[rel="canonical"]', "link", { rel: "canonical", href: canonical });
      upsert('meta[property="og:url"]', "meta", { property: "og:url", content: canonical });
    } else {
      remove('link[rel="canonical"]');
      remove('meta[property="og:url"]');
    }
    if (noindex) upsert('meta[name="robots"]', "meta", { name: "robots", content: "noindex, follow" });
    else remove('meta[name="robots"]');
  }, [t, d, canonical, noindex]);

  const trail = crumbs || (page ? pageCrumbs(path) : null);
  return trail && !noindex ? <JsonLd data={breadcrumbSchema(trail)} /> : null;
};

export default SEO;
